import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useSettings } from '../../contexts/SettingsContext';
import emailjs from 'emailjs-com';
import './Checkout.css';

function Checkout({ lang = 'es' }) {
  const navigate = useNavigate();
  const { cart, clearCart, ivaData } = useCart(); // Get IVA data from context
  const { catalogueMode } = useSettings();
  const [loading, setLoading] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null); // 'success', 'error', or null
  const [formErrors, setFormErrors] = useState({});
  const [savedCart, setSavedCart] = useState(null);
  const [savedSubtotal, setSavedSubtotal] = useState(0);
  const [savedIVA, setSavedIVA] = useState(0);
  const [savedTotal, setSavedTotal] = useState(0);
  
  // Form state
  const [checkoutForm, setCheckoutForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    province: '',
    canton: '',
    district: '',
    exactAddress: '',
    additionalInfo: '',
    paymentMethod: 'bankTransfer'
  });
  
  // Translation object
  const translations = {
    es: {
      checkoutTitle: "Finalizar compra",
      personalInfo: "Información personal",
      fullName: "Nombre completo",
      email: "Correo electrónico",
      phone: "Teléfono",
      address: "Dirección de entrega",
      province: "Provincia",
      canton: "Cantón",
      district: "Distrito",
      exactAddress: "Dirección exacta",
      additionalInfo: "Información adicional",
      paymentMethod: "Método de pago",
      bankTransfer: "Transferencia bancaria o SINPE Móvil",
      cash: "Efectivo",
      completeOrder: "Completar pedido",
      backToCart: "Volver al carrito",
      requiredField: "Campo requerido",
      invalidEmail: "Correo electrónico inválido",
      orderSent: "¡Pedido enviado!",
      orderSuccess: "Su pedido ha sido enviado con éxito. Pronto nos pondremos en contacto con usted.",
      orderError: "Ha ocurrido un error al enviar su pedido. Por favor intente nuevamente.",
      emptyCart: "Carrito vacío",
      emptyCartMessage: "Su carrito está vacío. Por favor agregue productos antes de proceder al pago.",
      continueShopping: "Continuar comprando",
      subtotal: "Subtotal",
      total: "Total",
      addressNote: "Esta información es importante para la entrega de su pedido, pero no es obligatoria para procesar la orden.",
      shippingNote: "Esta información nos ayudará a coordinar la entrega de sus productos.",
    },
    en: {
      checkoutTitle: "Checkout",
      personalInfo: "Personal information",
      fullName: "Full name",
      email: "Email",
      phone: "Phone",
      address: "Delivery address",
      province: "Province",
      canton: "Canton",
      district: "District",
      exactAddress: "Exact address",
      additionalInfo: "Additional information",
      paymentMethod: "Payment method",
      bankTransfer: "Bank transfer or SINPE",
      cash: "Cash",
      completeOrder: "Complete order",
      backToCart: "Back to cart",
      requiredField: "Required field",
      invalidEmail: "Invalid email",
      orderSent: "Order sent!",
      orderSuccess: "Your order has been sent successfully. We will contact you soon.",
      orderError: "An error occurred while sending your order. Please try again.",
      emptyCart: "Empty cart",
      emptyCartMessage: "Your cart is empty. Please add products before proceeding to checkout.",
      continueShopping: "Continue shopping",
      subtotal: "Subtotal",
      total: "Total",
      addressNote: "This information is important for shipping your order, but it's not required to process the order.",
      shippingNote: "This information will help us coordinate the delivery of your products.",
    }
  };
  
  const t = translations[lang] || translations.es;
  
  // Checkout is disabled in Catalogue Mode — the cart becomes a WhatsApp inquiry list.
  useEffect(() => {
    if (catalogueMode) {
      navigate('/cart', { replace: true });
    }
  }, [catalogueMode, navigate]);

  // Check if cart is empty and redirect to cart page if it is
  useEffect(() => {
    if (cart.length === 0 && orderStatus !== 'success') {
      navigate('/cart');
    }
  }, [cart, navigate, orderStatus]);
  
  // Initialize EmailJS with your user ID
  useEffect(() => {
    emailjs.init("v30CzqahwBgcPrfv7");
  }, []);
  
  // Form handling functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCheckoutForm({
      ...checkoutForm,
      [name]: value
    });
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    // Required fields - keep only the essential ones
    if (!checkoutForm.fullName.trim()) {
      errors.fullName = t.requiredField;
    }
    
    if (!checkoutForm.email.trim()) {
      errors.email = t.requiredField;
    } else if (!/\S+@\S+\.\S+/.test(checkoutForm.email)) {
      errors.email = t.invalidEmail;
    }
    
    if (!checkoutForm.phone.trim()) {
      errors.phone = t.requiredField;
    }
    
    // Remove address validation requirements
    // if (!checkoutForm.province.trim()) {
    //   errors.province = t.requiredField;
    // }
    
    // if (!checkoutForm.canton.trim()) {
    //   errors.canton = t.requiredField;
    // }
    
    // if (!checkoutForm.district.trim()) {
    //   errors.district = t.requiredField;
    // }
    
    // if (!checkoutForm.exactAddress.trim()) {
    //   errors.exactAddress = t.requiredField;
    // }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Helper functions to calculate prices using IVA data from context
  const getIvaPercentage = (item) => {
    if (!item.iva_id || !ivaData[item.iva_id]) {
      return 0;
    }
    return (ivaData[item.iva_id].porcentaje || 0) * 100;
  };
  
  const getIvaName = (item) => {
    if (!item.iva_id || !ivaData[item.iva_id]) {
      return 'N/A';
    }
    return ivaData[item.iva_id].nombre || 'N/A';
  };
  
  const getBasePrice = (item) => {
    if (item.quantity >= 24 && item.precio3) {
      return Number(item.precio3);
    } else if (item.quantity >= 12 && item.precio2) {
      return Number(item.precio2);
    } else {
      return Number(item.precio1);
    }
  };
  
  const getIvaAmount = (item) => {
    const totalPrice = getBasePrice(item); // This price already includes IVA
    const ivaPercentage = getIvaPercentage(item);
    
    if (ivaPercentage > 0) {
      // Calculate IVA from the total price (IVA included)
      // Formula: IVA = total_price * (iva_rate / (100 + iva_rate))
      return totalPrice * (ivaPercentage / (100 + ivaPercentage));
    }
    
    return 0;
  };
  
  // Add these calculation functions after the getIvaAmount function and before formatCartItems
  const calculateSubtotal = () => {
    return cart.reduce((total, item) => {
      const totalPrice = getBasePrice(item); // Price with IVA included
      const ivaAmount = getIvaAmount(item); // IVA portion of the price
      const subtotal = totalPrice - ivaAmount; // Price without IVA
      return total + (subtotal * item.quantity);
    }, 0);
  };

  const calculateTotalIVA = () => {
    return cart.reduce((total, item) => {
      const ivaAmount = getIvaAmount(item); // IVA portion per unit
      return total + (ivaAmount * item.quantity);
    }, 0);
  };

  const calculateTotal = () => {
    // Since IVA is already included in the base prices, total is just sum of base prices
    return cart.reduce((total, item) => {
      const totalPrice = getBasePrice(item);
      return total + (totalPrice * item.quantity);
    }, 0);
  };
  
  // Update the formatCartItems function to use color_name
  const formatCartItems = () => {
    let itemsHTML = '';
    let subtotal = 0;
    let totalIVA = 0;
    
    cart.forEach(item => {
      const totalPrice = getBasePrice(item); // Price with IVA included
      const ivaPercentage = getIvaPercentage(item);
      const ivaName = getIvaName(item);
      const ivaAmount = getIvaAmount(item); // IVA portion per unit
      const subtotalPerUnit = totalPrice - ivaAmount; // Price without IVA per unit
      const itemSubtotal = subtotalPerUnit * item.quantity;
      const itemIva = ivaAmount * item.quantity;
      
      subtotal += itemSubtotal;
      totalIVA += itemIva;
      
      // Use color_name instead of color_id
      const colorDisplay = item.color_name || `Color #${item.color_numeric_id}` || 'N/A';
      
      itemsHTML += `
        <tr>
          <td>${item.nombre} (${item.codigo})</td>
          <td>${colorDisplay}</td>
          <td>${item.talla_id || 'N/A'}</td>
          <td>${item.quantity}</td>
          <td>₡${totalPrice.toFixed(2)} (incluye IVA)</td>
          <td>${ivaName} (${ivaPercentage.toFixed(2)}%): ₡${ivaAmount.toFixed(2)}</td>
          <td>₡${itemSubtotal.toFixed(2)}</td>
          <td>₡${itemIva.toFixed(2)}</td>
        </tr>
      `;
    });
    
    return {
      itemsHTML,
      subtotal,
      totalIVA,
      total: subtotal + totalIVA
    };
  };
  
  // Modify your handleCheckoutSubmit function
  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();
    
    // Check if form is valid
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('.error-message');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    setLoading(true);
    
    // Save cart data before it's cleared
    setSavedCart([...cart]);
    setSavedSubtotal(calculateSubtotal());
    setSavedIVA(calculateTotalIVA());
    setSavedTotal(calculateTotal());
    
    try {
      const { subtotal, totalIVA, total } = formatCartItems();
      
      // Prepare email template parameters with IVA details
      const templateParams = {
        to_name: "CAMITEX",
        from_name: checkoutForm.fullName,
        from_email: checkoutForm.email,
        phone: checkoutForm.phone,
        province: checkoutForm.province,
        canton: checkoutForm.canton,
        district: checkoutForm.district,
        address: checkoutForm.exactAddress,
        
        // Try multiple variations of payment method parameters to ensure one works
        payment_method: checkoutForm.paymentMethod === 'bankTransfer' ? 'Transferencia bancaria o SINPE Móvil' : 'Efectivo',
        paymentMethod: checkoutForm.paymentMethod === 'bankTransfer' ? 'Transferencia bancaria o SINPE Móvil' : 'Efectivo',
        payment: checkoutForm.paymentMethod === 'bankTransfer' ? 'Transferencia bancaria o SINPE Móvil' : 'Efectivo',
        method: checkoutForm.paymentMethod === 'bankTransfer' ? 'Transferencia bancaria o SINPE Móvil' : 'Efectivo',
        
        // Add payment method with strong styling to ensure it's visible
        payment_html: `<div style="font-weight: bold; font-size: 16px; color: #28a745; margin: 10px 0;">
          ${checkoutForm.paymentMethod === 'bankTransfer' ? 'Transferencia bancaria o SINPE Móvil' : 'Efectivo'}
        </div>`,
        
        // Include payment method in the products_html to ensure it shows up somewhere
        products_html: `
          <div style="margin-bottom: 20px;">
            <h3 style="color: #28a745; border-bottom: 1px solid #ddd; padding-bottom: 10px;">Método de pago:</h3>
            <div style="padding: 10px; background-color: #f8f9fa; border-radius: 4px; font-weight: bold; font-size: 16px;">
              ${checkoutForm.paymentMethod === 'bankTransfer' ? 'Transferencia bancaria o SINPE Móvil' : 'Efectivo'}
            </div>
            
            <h3 style="color: #28a745; border-bottom: 1px solid #ddd; padding-bottom: 10px; margin-top: 30px;">Productos en su pedido:</h3>
            <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
              <thead>
                <tr style="background-color: #f8f9fa;">
                  <th style="padding: 10px; border: 1px solid #dee2e6; text-align: left;">Producto</th>
                  <th style="padding: 10px; border: 1px solid #dee2e6; text-align: center;">Color</th>
                  <th style="padding: 10px; border: 1px solid #dee2e6; text-align: center;">Talla</th>
                  <th style="padding: 10px; border: 1px solid #dee2e6; text-align: center;">Cantidad</th>
                  <th style="padding: 10px; border: 1px solid #dee2e6; text-align: right;">Precio</th>
                  <th style="padding: 10px; border: 1px solid #dee2e6; text-align: right;">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                ${cart.map(item => {
                  const totalPrice = getBasePrice(item); // Price with IVA included
                  const ivaAmount = getIvaAmount(item); // IVA portion per unit
                  const subtotalPerUnit = totalPrice - ivaAmount; // Price without IVA per unit
                  const itemSubtotal = subtotalPerUnit * item.quantity;
                  // Use color_name instead of color_id
                  const colorDisplay = item.color_name || `Color #${item.color_numeric_id}` || 'N/A';
                  return `
                    <tr>
                      <td style="padding: 10px; border: 1px solid #dee2e6;">
                        <div style="font-weight: bold;">${item.nombre}</div>
                        <div style="font-size: 12px; color: #666;">Código: ${item.codigo || 'N/A'}</div>
                      </td>
                      <td style="padding: 10px; border: 1px solid #dee2e6; text-align: center;">${colorDisplay}</td>
                      <td style="padding: 10px; border: 1px solid #dee2e6; text-align: center;">${item.talla_id || 'N/A'}</td>
                      <td style="padding: 10px; border: 1px solid #dee2e6; text-align: center;">${item.quantity}</td>
                      <td style="padding: 10px; border: 1px solid #dee2e6; text-align: right;">₡${totalPrice.toFixed(2)} (incluye IVA)</td>
                      <td style="padding: 10px; border: 1px solid #dee2e6; text-align: right;">₡${itemSubtotal.toFixed(2)}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
              <tfoot>
                <tr style="background-color: #f8f9fa;">
                  <td colspan="5" style="padding: 10px; border: 1px solid #dee2e6; text-align: right; font-weight: bold;">Subtotal:</td>
                  <td style="padding: 10px; border: 1px solid #dee2e6; text-align: right; font-weight: bold;">₡${subtotal.toFixed(2)}</td>
                </tr>
                <tr style="background-color: #f8f9fa;">
                  <td colspan="5" style="padding: 10px; border: 1px solid #dee2e6; text-align: right; font-weight: bold;">IVA Total:</td>
                  <td style="padding: 10px; border: 1px solid #dee2e6; text-align: right; font-weight: bold;">₡${totalIVA.toFixed(2)}</td>
                </tr>
                <tr style="background-color: #f8f9fa;">
                  <td colspan="5" style="padding: 10px; border: 1px solid #dee2e6; text-align: right; font-weight: bold; color: #e60026;">TOTAL:</td>
                  <td style="padding: 10px; border: 1px solid #dee2e6; text-align: right; font-weight: bold; color: #e60026;">₡${total.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        `,
        
        // Add explicit parameters for the totals (in case products_html doesn't work)
        subtotal_html: `<strong>Subtotal:</strong> ₡${subtotal.toFixed(2)}`,
        iva_total_html: `<strong>IVA Total:</strong> ₡${totalIVA.toFixed(2)}`,
        total_html: `<strong>TOTAL:</strong> ₡${total.toFixed(2)}`,
        
        // Also add simple text versions
        subtotal_text: subtotal.toFixed(2),
        iva_total_text: totalIVA.toFixed(2),
        total_text: total.toFixed(2)
      };
      
      // Send email using your EmailJS template
      await emailjs.send(
        "service_zz7oqhq",
        "template_uu2wtq4",
        templateParams
      );
      
      // Order sent successfully
      setOrderStatus('success');
      clearCart(); // Clear the cart after successful order
    } catch (error) {
      console.error('Error sending order:', error);
      setOrderStatus('error');
    } finally {
      setLoading(false);
    }
  };
  
  // If cart is empty and we're not in success state, show empty cart message
  if (cart.length === 0 && orderStatus !== 'success') {
    return (
      <div className="empty-cart-container">
        <h2>{t.emptyCart}</h2>
        <p>{t.emptyCartMessage}</p>
        <Link to="/catalogue" className="continue-shopping-btn">
          {t.continueShopping}
        </Link>
      </div>
    );
  }
  
  return (
    <div className="checkout-page">
      <h1>{t.checkoutTitle}</h1>
      
      {/* Order success message */}
      {orderStatus === 'success' ? (
        <div className="checkout-page">
          <div className="order-success">
            <div className="success-icon">
              <i className="fa fa-check-circle"></i>
            </div>
            <h2 className="success-title">{t.orderSent}</h2>
            <div className="order-confirmation">
              <p className="success-message">{t.orderSuccess}</p>
              
              <div className="confirmation-details">
                <h3 className="details-section-title">Detalles del pedido:</h3>
                
                <div className="customer-details">
                  <h4>Información del cliente</h4>
                  <div className="info-card">
                    <p><span className="info-label">Nombre:</span> {checkoutForm.fullName}</p>
                    <p><span className="info-label">Email:</span> {checkoutForm.email}</p>
                    <p><span className="info-label">Teléfono:</span> {checkoutForm.phone}</p>
                  </div>
                </div>
                
                <div className="address-details">
                  <h4>Dirección de entrega</h4>
                  <div className="info-card">
                    <p>
                      <span className="info-label">Provincia:</span> {checkoutForm.province}<br />
                      <span className="info-label">Cantón:</span> {checkoutForm.canton}<br />
                      <span className="info-label">Distrito:</span> {checkoutForm.district}<br />
                      <span className="info-label">Dirección exacta:</span> {checkoutForm.exactAddress}
                    </p>
                  </div>
                </div>
                
                <div className="payment-details">
                  <h4>Método de pago</h4>
                  <div className="info-card payment-method">
                    <p>{checkoutForm.paymentMethod === 'bankTransfer' ? t.bankTransfer : t.cash}</p>
                  </div>
                </div>
                
                <div className="order-products">
                  <h4>Productos en su pedido</h4>
                  <div className="products-table-container">
                    <table className="products-table">
                      <thead>
                        <tr>
                          <th>Producto</th>
                          <th>Color</th>
                          <th>Talla</th>
                          <th>Cantidad</th>
                          <th>Precio Unit.</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Use original cart saved before clearing */}
                        {savedCart && savedCart.map((item, index) => {
                          // Calculate prices correctly with IVA included
                          const totalPrice = getBasePrice(item); // Price with IVA included
                          const ivaAmount = getIvaAmount(item); // IVA portion per unit
                          const subtotalPerUnit = totalPrice - ivaAmount; // Price without IVA per unit
                          const itemSubtotal = subtotalPerUnit * item.quantity;
                          // Use color_name instead of color_id
                          const colorDisplay = item.color_name || `Color #${item.color_numeric_id}` || 'N/A';
                          return (
                            <tr key={index}>
                              <td className="product-name">
                                <div className="product-title">{item.nombre}</div>
                                <div className="product-code">Código: {item.codigo || 'N/A'}</div>
                              </td>
                              <td>{colorDisplay}</td>
                              <td>{item.talla_id || 'N/A'}</td>
                              <td className="text-center">{item.quantity}</td>
                              <td className="text-right">₡{totalPrice.toFixed(2)} (incluye IVA)</td>
                              <td className="text-right">₡{itemSubtotal.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="summary-row subtotal-row">
                          <td colSpan="5" className="text-right">Subtotal:</td>
                          <td className="text-right">₡{(savedSubtotal || 0).toFixed(2)}</td>
                        </tr>
                        <tr className="summary-row iva-row">
                          <td colSpan="5" className="text-right">IVA:</td>
                          <td className="text-right">₡{(savedIVA || 0).toFixed(2)}</td>
                        </tr>
                        <tr className="summary-row total-row">
                          <td colSpan="5" className="text-right">TOTAL:</td>
                          <td className="text-right">₡{(savedTotal || 0).toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
                
                <div className="next-steps-container">
                  <h4>Próximos pasos</h4>
                  <div className="info-card next-steps">
                    <p>Un representante de CAMITEX se pondrá en contacto con usted para coordinar el pago y la entrega.</p>
                    <p className="contact-info">Si tiene alguna pregunta, puede contactarnos por WhatsApp al <a href="https://wa.me/50687839222" className="whatsapp-link">+506 8783 9222</a></p>
                  </div>
                </div>
              </div>
              
              <div className="order-actions">
                <Link to="/" className="continue-shopping-btn">
                  {t.continueShopping}
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="checkout-container">
          {orderStatus === 'error' && (
            <div className="order-error">
              {t.orderError}
            </div>
          )}
          
          <form onSubmit={handleCheckoutSubmit} className="checkout-form">
            <div className="form-section">
              <h2>{t.personalInfo}</h2>
              <div className="form-group">
                <label htmlFor="fullName">{t.fullName} *</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={checkoutForm.fullName}
                  onChange={handleInputChange}
                  className={formErrors.fullName ? 'error' : ''}
                />
                {formErrors.fullName && <div className="error-message">{formErrors.fullName}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="email">{t.email} *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={checkoutForm.email}
                  onChange={handleInputChange}
                  className={formErrors.email ? 'error' : ''}
                />
                {formErrors.email && <div className="error-message">{formErrors.email}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">{t.phone} *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={checkoutForm.phone}
                  onChange={handleInputChange}
                  className={formErrors.phone ? 'error' : ''}
                />
                {formErrors.phone && <div className="error-message">{formErrors.phone}</div>}
              </div>
            </div>
            
            <div className="form-section">
              <h2>{t.address}</h2>
              <p className="address-note">{t.addressNote}</p>
              
              <div className="form-group">
                <label htmlFor="province">{t.province}</label>
                <input
                  type="text"
                  id="province"
                  name="province"
                  value={checkoutForm.province}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="canton">{t.canton}</label>
                <input
                  type="text"
                  id="canton"
                  name="canton"
                  value={checkoutForm.canton}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="district">{t.district}</label>
                <input
                  type="text"
                  id="district"
                  name="district"
                  value={checkoutForm.district}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="exactAddress">{t.exactAddress}</label>
                <textarea
                  id="exactAddress"
                  name="exactAddress"
                  value={checkoutForm.exactAddress}
                  onChange={handleInputChange}
                />
                <small className="shipping-note">{t.shippingNote}</small>
              </div>
            </div>
            
            <div className="form-section">
              <h2>{t.additionalInfo}</h2>
              <div className="form-group">
                <label htmlFor="additionalInfo">{t.additionalInfo}</label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={checkoutForm.additionalInfo}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            
            <div className="form-section">
              <h2>{t.paymentMethod}</h2>
              <div className="form-group radio-group">
                <div className="radio-option">
                  <input
                    type="radio"
                    id="bankTransfer"
                    name="paymentMethod"
                    value="bankTransfer"
                    checked={checkoutForm.paymentMethod === 'bankTransfer'}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="bankTransfer">{t.bankTransfer}</label>
                </div>
                <div className="radio-option">
                  <input
                    type="radio"
                    id="cash"
                    name="paymentMethod"
                    value="cash"
                    checked={checkoutForm.paymentMethod === 'cash'}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="cash">{t.cash}</label>
                </div>
              </div>
            </div>
            
            <div className="order-summary">
              <h2>{t.subtotal}: ₡{calculateSubtotal().toFixed(2)}</h2>
              <h3>IVA: ₡{calculateTotalIVA().toFixed(2)}</h3>
              <h2>{t.total}: ₡{calculateTotal().toFixed(2)}</h2>
            </div>
            
            <div className="checkout-actions">
              <Link to="/cart" className="back-to-cart-btn">
                {t.backToCart}
              </Link>
              <button 
                type="submit" 
                className="complete-order-btn"
                disabled={loading}
              >
                {loading ? '...' : t.completeOrder}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default Checkout;